import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import { CompareTable } from './FinancialsSynthesisSimple';
import Card from '../components/Card';
import { useForecastData, buildForecastModel, sum12, buildTreasury } from './forecastModel';
import './ForecastSummary.css';
import './FinancialsSynthesisTab.css';   // tuiles KPI (fsyn-tile) — même style que CFO / Cockpit

/* Synthèse prévisionnelle — même format que la synthèse Rapports
   (CompareTable : blocs condensés, en-tête 2 niveaux). En vue forward, le
   comparatif est en ANNÉE PLEINE : Total prévisionnel [N] · Total réel [N-1] ·
   écart CHF · écart %. En tête, des KPI (EPT · revenus prestations annualisé /
   EPT) à fin de mois clôturé et en total annuel vs N-1. Pas d'édition ici. */

function Tile({ label, value, sub }) {
  // Ligne de sous-texte toujours rendue (insécable si vide) → hauteur homogène.
  return (
    <div className="fsyn-tile">
      <div className="fsyn-tile__label">{label}</div>
      <div className="fsyn-tile__value">{value}</div>
      <div className="fsyn-tile__delta">{sub != null ? sub : ' '}</div>
    </div>
  );
}

export default function ForecastSummary({ activeTenant, year, assumptions, cashflow, unit = 'kchf', onReady }) {
  const { t } = useTranslation();
  const p = useP();
  const monthLabels = useMemo(
    () => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)),
    [t],
  );
  const monthFull = useMemo(
    () => Array.from({ length: 12 }, (_, i) => t(`common:monthsFull.${i + 1}`)),
    [t],
  );
  const { plView, bsCur, bsPrev, bsPrev2, plPrev, fteCur, ftePrev, loading } = useForecastData(activeTenant?.id, year);
  const m = useMemo(
    () => buildForecastModel(plView, bsCur, bsPrev, plPrev, assumptions, { bsPrev2, fteCur, ftePrev }),
    [plView, bsCur, bsPrev, bsPrev2, plPrev, fteCur, ftePrev, assumptions.dso, assumptions.dpo, assumptions.dio, assumptions.pac],
  );
  useEffect(() => { if (!loading && onReady) onReady(); }, [loading]); // eslint-disable-line

  if (loading && !m) return <p className="fps-loading">{p('Chargement de la synthèse…')}</p>;
  if (!m) return <p className="fps-loading">{p('Aucune donnée prévisionnelle pour cette année.')}</p>;

  const pnl = m.pnl, n1 = m.pnlN1, k = m.kpi;
  const money = (v) => (v == null || !Number.isFinite(v) ? '—'
    : `${Math.round(Number(v) / (unit === 'chf' ? 1 : 1000)).toLocaleString('de-CH')} ${unit === 'chf' ? 'CHF' : 'kCHF'}`);
  const pct1 = (v) => (v == null || !Number.isFinite(v) ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)} %`);
  const monthName = k.eptSpot != null || m.closedCount > 0 ? monthFull[Math.max(0, Math.min(11, m.closedCount - 1))] : null;

  // Compte de résultat : 12 mois individuels de l'année, « réel » (mois clos) /
  // « prév. » (forecast) sous chaque mois · Total · écart vs N-1.
  const cc = m.closedCount;
  const allIdx = Array.from({ length: 12 }, (_, i) => i);
  const delta = (a, b) => (a != null && b != null ? { abs: a - b, pct: b !== 0 ? ((a - b) / Math.abs(b)) * 100 : null } : null);
  const pnlGroups = [
    { label: `${year}`, cols: [
      ...allIdx.map((i) => ({ key: `m${i}`, label: monthLabels[i], sub: i < cc ? p('réel') : p('prév.'), sepBefore: i === cc && cc > 0 })),
      { key: 'tot', label: p('Total'), highlight: true },
    ] },
    { label: `${p('Écart')} ${year - 1}`, sep: true, cols: [
      { key: 'n1', label: `${year - 1}` },
      { key: 'deltaAbs', label: 'CHF' },
      { key: 'deltaPct', label: '%' },
    ] },
  ];
  const rowM12 = (label, arr, n1v, kind) => {
    const tot = sum12(arr);
    const o = { label, tot, n1: n1v == null ? undefined : n1v, kind, delta: delta(tot, n1v) };
    allIdx.forEach((i) => { o[`m${i}`] = arr ? arr[i] : null; });
    return o;
  };

  // Prévisionnel de trésorerie : variation totale + soldes début/fin par mois (12),
  // sans colonne d'écart N-1. Même cascade que l'onglet Cash flow (helper partagé).
  const tr = buildTreasury(m.cash, cashflow, cc);
  const trGroups = [
    { label: `${year}`, cols: [
      ...allIdx.map((i) => ({ key: `m${i}`, label: monthLabels[i], sub: i < cc ? p('réel') : p('prév.'), sepBefore: i === cc && cc > 0 })),
      { key: 'tot', label: p('Total'), highlight: true },
    ] },
    { label: `${p('Écart')} ${year - 1}`, sep: true, cols: [
      { key: 'n1', label: `${year - 1}` },
      { key: 'deltaAbs', label: 'CHF' },
      { key: 'deltaPct', label: '%' },
    ] },
  ];
  const trRow = (label, arr, kind, totalOverride, n1v) => {
    const tot = totalOverride === undefined ? sum12(arr) : totalOverride;
    const o = { label, kind, tot, n1: n1v == null ? undefined : n1v, delta: delta(tot, n1v) };
    allIdx.forEach((i) => { o[`m${i}`] = arr ? arr[i] : null; });
    return o;
  };
  // Valeurs N-1 (année pleine 2025) de la cascade cash — mêmes champs que l'onglet Cash flow.
  const cN1 = m.cash || {};
  const cafN1 = (cN1.netResultN1 != null && cN1.amortN1 != null) ? Number(cN1.netResultN1) + Number(cN1.amortN1) : null;
  // Synthèse cash flow (mêmes flux que l'onglet Cash flow) : CAF (résultat net +
  // amortissements) · CF opérationnel · investissement · financement · variation ·
  // solde final. CAF = information (composante de l'opérationnel), non re-sommée.
  const caf = allIdx.map((i) => {
    const nr = m.cash.netResult?.[i]; const am = m.cash.amort?.[i];
    return (nr == null && am == null) ? null : (Number(nr) || 0) + (Number(am) || 0);
  });
  const treasuryRows = [
    trRow(p("Capacité d'autofinancement"), caf, 'sub', undefined, cafN1),
    trRow(p('Cash flow opérationnel'), m.cash.opCf, undefined, undefined, cN1.opCfFullN1 ?? cN1.opCfN1 ?? null),
    trRow(p('Cash flow des investissements'), tr.investSub, undefined, undefined, cN1.investingN1 ?? null),
    trRow(p('Cash flow du financement'), tr.financingSub, undefined, undefined, cN1.financingN1 ?? null),
    trRow(p('Variation de trésorerie'), tr.totalVar, 'total', undefined, cN1.totalN1 ?? null),
    trRow(p('Solde final de trésorerie'), tr.soldeFin, 'sub', tr.soldeFin[11], cN1.oLiq ?? null),
  ];

  const pnlRows = [
    rowM12(p('Marchandises'), pnl.revMarchandises, n1?.revMarchandises),
    rowM12(p('Prestations'), pnl.revPrestations, n1?.revPrestations),
    rowM12(p('Autres'), pnl.revAutres, n1?.revAutres),
    rowM12(p('Chiffre d’affaires net'), pnl.revenue, n1?.revenue, 'sub'),
    rowM12(p('Coût des ventes'), pnl.cogs, n1?.cogs),
    rowM12(p('Valeur ajoutée'), pnl.valueAdded, n1?.valueAdded, 'sub'),
    rowM12(p('Charges de personnel'), pnl.personnel, n1?.personnel),
    rowM12(p('Marge brute'), pnl.grossMargin, n1?.grossMargin, 'sub'),
    rowM12(p('Charges indirectes'), pnl.indirect, n1?.indirect),
    rowM12(p('EBITDA'), pnl.ebitda, n1?.ebitda, 'total'),
  ];
  return (
    <div className="fps-synth fps-synth--prev">
      {/* ── Indicateurs-clés : revenu par EPT (à fin · à venir · moyenne annuelle)
             + solde final de trésorerie. Croissance annuelle du CA vs N-1 sur la
             droite (si N-1 disponible). ── */}
      <Card className="report-card" title={t('phrases:p_02359e2c26')}
            subtitle={`${p('prévisionnel')} ${year} · ${unit === 'chf' ? 'CHF' : 'kCHF'}`}>
        <div className="fps-eptkpis">
          <Tile label={`${p('Revenu/EPT à fin')} ${monthName || '—'} ${year}`} value={money(k.revPerEptSpot)} />
          <Tile label={`${p('Revenu/EPT à venir')} (${cc < 12 ? `${monthLabels[cc]}–${monthLabels[11]}` : '—'})`} value={money(k.revPerEptForward)} />
          <Tile label={p('Revenu/EPT moyenne annuelle')} value={money(k.revPerEptTot)} sub={`${year - 1} : ${money(k.revPerEptN1)}`} />
          <Tile label={t('phrases:p_aa9e2afcf7')}
                value={money(tr.soldeFin[11])}
                sub={`${tr.soldeFin[11] - tr.oLiq >= 0 ? '+' : ''}${money(tr.soldeFin[11] - tr.oLiq)} · ${p('initial')} ${money(tr.oLiq)}`} />
          {k.revGrowth != null && (
            <>
              <div className="fps-eptkpis__spacer" />
              <Tile label={`${p('Croissance CA vs')} ${year - 1}`} value={pct1(k.revGrowth)}
                    sub={`${money(k.revFy)} vs ${money(k.revN1)}`} />
            </>
          )}
        </div>
      </Card>

      <Card className="report-card" title={t('phrases:p_8e0f28455c')}
            subtitle={`${p('12 mois')} ${year} ${p('(réel + prév.) · total · vs')} ${year - 1} · ${p('écart')}`}>
        <CompareTable groups={pnlGroups} rows={pnlRows} unit={unit} compact />
      </Card>

      <Card className="report-card" title={t('phrases:p_a5643f4671')}
            subtitle={`${p('synthèse cash flow · 12 mois')} ${year} ${p('(réel + prév.)')}`}>
        <CompareTable groups={trGroups} rows={treasuryRows} unit={unit} compact />
      </Card>
    </div>
  );
}
